/**
 * wlGlobalInterceptor — Interceptor GLOBAL e SÍNCRONO de axios + fetch.
 *
 * Por que existe:
 *   - O `WhiteLabelLayout` instalava o interceptor dentro de um `useEffect`,
 *     que roda DEPOIS do primeiro render. Páginas master (GestaoUsuariosPage,
 *     ContratosPage etc.) disparam `axios.get(/api/usuarios)` no PRÓPRIO
 *     `useEffect` delas, que executa ANTES do effect do layout (regra do
 *     React: effects de filhos rodam antes dos do pai).
 *   - Resultado: a PRIMEIRA listagem em /wl/<slug>/dashboard/usuarios
 *     vazava sem tenant_id, expondo usuários da plataforma mãe.
 *
 * Solução: instalar interceptors no MOMENTO em que o módulo é importado
 * (carregamento do app). Eles ficam ativos a partir do primeiro tick e
 * decidem em tempo real, a partir do `window.location.pathname`, se a URL
 * é um contexto White Label (`/wl/<slug>/...`) e qual o tenant atual
 * (lido de `localStorage.wl_context`).
 *
 * Princípios:
 *   - Idempotente: instala uma única vez por sessão (guard via window flag).
 *   - Não-invasivo: só injeta `tenant_id` em chamadas /api/* QUANDO o usuário
 *     está numa rota /wl/* AND existe wl_context no localStorage. Fora desse
 *     contexto, não faz NADA.
 *   - Defensivo: pula injeção quando a URL já tem `tenant_id=` (respeitando
 *     chamadas explícitas do admin).
 */

import axios from 'axios';

const FLAG = '__wlGlobalInterceptorInstalled';

const isWlPath = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  // /wl/hubworkconnect/* é soft-label (não WL) — landing/acesso próprios, sem tenant.
  if (p.startsWith('/wl/hubworkconnect')) return false;
  return p.startsWith('/wl/');
};

const getWlTenant = () => {
  try {
    const ctx = localStorage.getItem('wl_context');
    if (!ctx) return null;
    const parsed = JSON.parse(ctx);
    return parsed?.tenant_id || null;
  } catch {
    return null;
  }
};

const getWlToken = () => {
  try {
    // Extrai slug do path /wl/<slug>/...
    const m = window.location.pathname.match(/^\/wl\/([^/]+)/);
    if (!m) return null;
    return localStorage.getItem(`wl_token_${m[1]}`);
  } catch {
    return null;
  }
};

const isApiUrl = (input) => {
  try {
    const url = typeof input === 'string' ? input : (input?.url || '');
    return /(^|\/)api\//.test(url) || url.includes('/api/');
  } catch {
    return false;
  }
};

/**
 * Endpoints "bootstrap" que precisam funcionar ANTES de termos wl_context
 * carregado no localStorage. Estes não recebem injeção nem bloqueio.
 *  - /api/white-label/preview/<slug>  -> carrega o franqueado
 *  - /api/white-label/franqueados/...  -> rotas admin (admin platform)
 *  - /api/auth-local/login            -> login
 *  - /api/auth/login                  -> compatibilidade
 *  - /api/usuarios/cadastro-rapido    -> registro publico
 *  - /api/usuarios/links/...          -> resolução de link público
 */
const isBootstrapApi = (input) => {
  try {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const path = url.split('?')[0];
    if (path.includes('/api/white-label/preview/')) return true;
    if (path.includes('/api/white-label/franqueados')) return true;
    if (path.includes('/api/white-label/clonar-landing')) return true;
    if (path.includes('/api/auth-local/login')) return true;
    if (path.includes('/api/auth/login')) return true;
    if (path.includes('/api/usuarios/cadastro-rapido')) return true;
    if (path.includes('/api/usuarios/links/')) return true;
    if (path.includes('/api/configuracoes/empresa')) return true;
    return false;
  } catch {
    return false;
  }
};

const injectTenantInUrl = (url, tenantId) => {
  try {
    const u = new URL(url, window.location.origin);
    if (!u.searchParams.has('tenant_id')) {
      u.searchParams.set('tenant_id', tenantId);
    }
    if (u.origin === window.location.origin) {
      return u.pathname + u.search + u.hash;
    }
    return u.toString();
  } catch {
    if (url.includes('tenant_id=')) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}tenant_id=${tenantId}`;
  }
};

export const installWlGlobalInterceptor = () => {
  if (typeof window === 'undefined') return;
  if (window[FLAG]) return; // idempotente
  window[FLAG] = true;

  // ---------- AXIOS ----------
  axios.interceptors.request.use((config) => {
    try {
      if (!isWlPath()) return config;
      const url = config.url || '';
      if (!isApiUrl(url)) return config;
      // Bootstrap endpoints: deixa passar sem injetar nem bloquear
      if (isBootstrapApi(url)) return config;

      const tenantId = getWlTenant();
      // SEGURANÇA CRÍTICA: em /wl/* sem wl_context, NUNCA deixa passar
      // chamadas /api/* não-bootstrap. Cancela a request — melhor falhar
      // do que vazar dados da plataforma mãe.
      if (!tenantId) {
        const cancelMsg = '[wlGlobalInterceptor] BLOCKED axios — em /wl/* sem wl_context: ' + url;
        console.warn(cancelMsg);
        throw new axios.Cancel(cancelMsg);
      }

      config.params = config.params || {};
      const alreadyHasTenant =
        ('tenant_id' in config.params) ||
        url.includes('tenant_id=');
      if (!alreadyHasTenant) {
        config.params.tenant_id = tenantId;
      }

      const token = getWlToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (e) {
      if (e instanceof axios.Cancel) throw e; // propaga o cancel
      console.warn('[wlGlobalInterceptor axios] erro:', e);
    }
    return config;
  });

  // ---------- FETCH ----------
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    try {
      if (!isWlPath()) return originalFetch(input, init);

      const originalUrl = typeof input === 'string' ? input : (input?.url || '');
      if (!isApiUrl(originalUrl)) return originalFetch(input, init);
      // Bootstrap endpoints: deixa passar sem injetar
      if (isBootstrapApi(originalUrl)) return originalFetch(input, init);

      const tenantId = getWlTenant();
      if (!tenantId) {
        console.warn('[wlGlobalInterceptor fetch] BLOCKED — em /wl/* sem wl_context:', originalUrl);
        return new Response(
          JSON.stringify({ detail: 'WL context não carregado ainda. Recarregue a página.' }),
          { status: 401, headers: { 'Content-Type': 'application/json' } }
        );
      }

      const newUrl = injectTenantInUrl(originalUrl, tenantId);
      const token = getWlToken();
      const newInit = { ...init, headers: { ...(init.headers || {}) } };
      if (token && !newInit.headers.Authorization && !newInit.headers.authorization) {
        newInit.headers.Authorization = `Bearer ${token}`;
      }

      if (typeof input !== 'string' && input instanceof Request) {
        const newReq = new Request(newUrl, input);
        return originalFetch(newReq, newInit);
      }
      return originalFetch(newUrl, newInit);
    } catch (e) {
      console.warn('[wlGlobalInterceptor fetch] erro:', e);
      return originalFetch(input, init);
    }
  };

  console.log('[wlGlobalInterceptor] instalado (axios + fetch).');
};

export default installWlGlobalInterceptor;
